import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import subscriptionAPI from '../services/subscriptionAPI';
import { institute_id } from '../config/config';

const SubscriptionContext = createContext(null);

/**
 * Multi-Tenant Subscription Context
 * Manages subscription state for current institute only
 * Subscriptions are isolated per institute
 */
export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current institute ID from user or config
  const getCurrentInstituteId = useCallback(() => {
    return user?.institute_id || institute_id;
  }, [user]);

  /**
   * Fetch available plans for current institute
   */
  const fetchPlans = useCallback(async () => {
    try {
      const response = await subscriptionAPI.getPlans();
      if (response.data.success) {
        setPlans(response.data.plans || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans');
    }
  }, []);

  /**
   * Fetch current subscription for user at current institute
   */
  const fetchCurrentSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      return;
    }

    try {
      setLoading(true);
      const response = await subscriptionAPI.getCurrentSubscription(user.id);
      if (response.data.success) {
        setSubscription(response.data.subscription);
      } else {
        console.log('No subscription found');
        setSubscription(null);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch usage statistics for user at current institute
   */
  const fetchUsageStats = useCallback(async () => {
    if (!user?.id) {
      setUsageStats(null);
      return;
    }

    try {
      const response = await subscriptionAPI.getUsageStats(user.id);
      
      if (response.data.success) {
        setUsageStats({
          limits: response.data.limits,
          totalUsage: response.data.total_usage,
          usageByFeature: response.data.usage_by_feature
        });
      }
    } catch (err) {
      console.error('Error fetching usage stats:', err);
    }
  }, [user]);

  /**
   * Check if user has access to a specific feature at current institute
   * @param {string} featureType - Feature to check
   * @returns {Promise<Object>} Access status and details
   */
  const checkFeatureAccess = useCallback(async (featureType) => {
    if (!user?.id) {
      return {
        has_access: false,
        reason: 'not_logged_in',
        message: 'Please log in to access AI features'
      };
    }

    try {
      const response = await subscriptionAPI.checkFeatureAccess(user.id, featureType);
      return response.data;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return {
        has_access: false,
        reason: 'error',
        message: 'Error checking access'
      };
    }
  }, [user]);

  /**
   * Log feature usage at current institute
   * @param {string} featureType - Feature used
   * @param {Object} metadata - Additional usage data
   */
  const logFeatureUsage = useCallback(async (featureType, metadata = {}) => {
    if (!user?.id) return;

    try {
      await subscriptionAPI.logUsage({
        user_id: user.id,
        feature_type: featureType,
        metadata
      });
      
      // Refresh usage stats after logging
      await fetchUsageStats();
    } catch (err) {
      console.error('Error logging usage:', err);
    }
  }, [user, fetchUsageStats]);

  /**
   * Subscribe to a plan at current institute
   * @param {Object} subscriptionData - {plan_id, billing_cycle}
   */
  const subscribeToPlan = useCallback(async (subscriptionData) => {
    if (!user?.id) {
      throw new Error('User not logged in');
    }

    try {
      // Create payment order
      const orderResponse = await subscriptionAPI.createPaymentOrder({
        user_id: user.id,
        plan_id: subscriptionData.plan_id,
        billing_cycle: subscriptionData.billing_cycle
      });

      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Failed to create payment order');
      }

      const { order_id, amount, currency, razorpay_key } = orderResponse.data;

      // Open Razorpay payment modal
      const options = {
        key: razorpay_key,
        amount: amount * 100,
        currency: currency,
        name: 'BrainersHub AI Subscription',
        description: `${subscriptionData.billing_cycle} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await subscriptionAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: user.id,
              plan_id: subscriptionData.plan_id,
              billing_cycle: subscriptionData.billing_cycle
            });

            if (verifyResponse.data.success) {
              // Refresh subscription data
              await fetchCurrentSubscription();
              await fetchUsageStats();
              
              if (subscriptionData.onSuccess) {
                subscriptionData.onSuccess(verifyResponse.data);
              }
            } else {
              throw new Error(verifyResponse.data.message || 'Payment verification failed');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            if (subscriptionData.onError) {
              subscriptionData.onError(err);
            }
          }
        },
        prefill: {
          name: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
          email: user.email || '',
          contact: user.mobileno || ''
        },
        theme: {
          color: '#3b82f6'
        },
        modal: {
          ondismiss: function() {
            if (subscriptionData.onCancel) {
              subscriptionData.onCancel();
            }
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Subscribe error:', err);
      throw err;
    }
  }, [user, fetchCurrentSubscription, fetchUsageStats]);

  /**
   * Create payment order (for direct API access)
   * @param {Object} data - {plan_id, billing_cycle}
   * @returns {Promise<Object>} Order details
   */
  const createPaymentOrder = useCallback(async (data) => {
    if (!user?.id) {
      throw new Error('User not logged in');
    }

    try {
      const response = await subscriptionAPI.createPaymentOrder({
        user_id: user.id,
        ...data
      });

      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    } catch (err) {
      console.error('Create payment order error:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to create payment order'
      };
    }
  }, [user]);

  /**
   * Verify payment and activate subscription (for direct API access)
   * @param {Object} data - Payment verification data
   * @returns {Promise<Object>} Verification result
   */
  const verifyAndActivate = useCallback(async (data) => {
    if (!user?.id) {
      throw new Error('User not logged in');
    }

    try {
      const response = await subscriptionAPI.verifyPayment({
        user_id: user.id,
        ...data
      });

      if (response.data.success) {
        // Refresh subscription data
        await fetchCurrentSubscription();
        await fetchUsageStats();
      }

      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    } catch (err) {
      console.error('Verify payment error:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Payment verification failed'
      };
    }
  }, [user, fetchCurrentSubscription, fetchUsageStats]);

  /**
   * Cancel current subscription at current institute
   * @param {string} reason - Cancellation reason
   */
  const cancelSubscription = useCallback(async (reason = '') => {
    if (!subscription?.id) {
      throw new Error('No active subscription');
    }

    try {
      const response = await subscriptionAPI.cancelSubscription(subscription.id, {
        user_id: user.id,
        reason
      });

      if (response.data.success) {
        // Refresh subscription data
        await fetchCurrentSubscription();
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      throw err;
    }
  }, [subscription, user, fetchCurrentSubscription]);

  /**
   * Get usage for specific feature at current institute
   * @param {string} featureType - Feature type
   * @returns {Object} Usage data {used, limit, remaining, percentage}
   */
  const getFeatureUsage = useCallback((featureType) => {
    if (!usageStats?.limits) return null;
    return usageStats.limits[featureType] || null;
  }, [usageStats]);

  /**
   * Check if user has active subscription at current institute
   * @returns {boolean}
   */
  const hasActiveSubscription = useCallback(() => {
    return subscription !== null && subscription.status === 'active';
  }, [subscription]);

  /**
   * Get current subscription tier at current institute
   * @returns {string} free, basic, premium, or enterprise
   */
  const getCurrentTier = useCallback(() => {
    return subscription?.tier || 'free';
  }, [subscription]);

  /**
   * Check if feature is available in current tier at current institute
   * @param {string} featureType - Feature to check
   * @returns {boolean}
   */
  const isFeatureInTier = useCallback((featureType) => {
    if (!subscription?.features) return false;
    return subscription.features.includes(featureType);
  }, [subscription]);

  /**
   * Get current tier information
   * @returns {Object} Tier info with name, features, limits
   */
  const getTierInfo = useCallback(() => {
    console.log('getTierInfo called, subscription:', subscription);
    
    if (!subscription || !subscription.id) {
      // Return actual FREE tier info with features and limits
      return {
        name: 'Free Tier',
        tier: 'free',
        features: ['quiz_generator', 'notes_generator'],
        limits: {
          quiz_generator: 5,
          notes_generator: 3
        },
        description: 'Free tier with limited features'
      };
    }

    const tierInfo = {
      name: subscription.plan_name || 'Unknown',
      tier: subscription.tier || 'free',
      features: subscription.features || [],
      limits: subscription.limits || {},
      description: subscription.description || '',
      billingCycle: subscription.billing_cycle || 'monthly',
      endDate: subscription.end_date,
      daysRemaining: subscription.days_remaining
    };
    
    console.log('Returning tierInfo:', tierInfo);
    
    return tierInfo;
  }, [subscription]);

  // Load data on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      fetchCurrentSubscription();
      fetchUsageStats();
      fetchPlans();
    } else {
      setSubscription(null);
      setUsageStats(null);
      setLoading(false);
    }
  }, [user, fetchCurrentSubscription, fetchUsageStats, fetchPlans]);

  const value = {
    // State
    subscription,
    usageStats,
    plans,
    loading,
    error,
    
    // Institute info
    currentInstituteId: getCurrentInstituteId(),
    
    // Actions
    fetchCurrentSubscription,
    fetchUsageStats,
    fetchPlans,
    checkFeatureAccess,
    logFeatureUsage,
    subscribeToPlan,
    createPaymentOrder,
    verifyAndActivate,
    cancelSubscription,
    
    // Helpers
    getFeatureUsage,
    hasActiveSubscription,
    getCurrentTier,
    isFeatureInTier,
    getTierInfo
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to use subscription context
 * @returns {Object} Subscription context value
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
